import { EarningsPeriod } from '@api';
import { Chip } from '@design/components/chip/Chip';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { DropdownEntry } from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { DropdownRadioGroup } from '@design/components/dropdown-menu/dropdown-radio-group/DropdownRadioGroup';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ReportingDates.module.scss';
import { getReportingPeriodLabel, getReportingPeriodTimeframe } from './utils';

export type ReportingPeriod = Pick<
  EarningsPeriod,
  'fiscal_period' | 'fiscal_year' | 'reporting_date'
>;

export type ReportingDatesProps<T extends ReportingPeriod> = {
  reportingPeriods: T[];
  activeReportingPeriod: T;
  onActiveReportingPeriodChange: (reportingPeriod: T) => void;
};

export const ReportingDates = <T extends ReportingPeriod>({
  reportingPeriods,
  activeReportingPeriod,
  onActiveReportingPeriodChange,
}: ReportingDatesProps<T>) => {
  const { t } = useTranslation(['earnings']);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getTimeframeLabel = (period: ReportingPeriod) => {
    const timeframe = getReportingPeriodTimeframe(period);
    return timeframe
      ? t('earnings:reportingDates.timeframe', timeframe)
      : undefined;
  };

  const periodItems: DropdownEntry[] = reportingPeriods.map(
    reportingPeriod => ({
      key: reportingPeriod.reporting_date,
      label: getReportingPeriodLabel(reportingPeriod),
      tooltip: getTimeframeLabel(reportingPeriod),
      onClick: () => {
        onActiveReportingPeriodChange(reportingPeriod);
        setIsDropdownOpen(false);
      },
    })
  );

  const timeframeLabel = getTimeframeLabel(activeReportingPeriod);

  return (
    <div className={styles.root} data-testid="reporting-dates">
      <DropdownMenu
        open={isDropdownOpen}
        onOpenChange={setIsDropdownOpen}
        className={styles.menuWrapper}
        triggerNode={
          <Chip
            className={styles.chip}
            label={
              <Tooltip
                label={timeframeLabel ?? ''}
                trigger={TooltipTrigger.Hover}
                disabled={isDropdownOpen || !timeframeLabel}
              >
                <div className={styles.labelContainer}>
                  <span className={styles.label}>
                    {t('earnings:reportingDates.label')}
                  </span>
                  <span className={styles.activePeriod}>
                    {getReportingPeriodLabel(activeReportingPeriod)}
                  </span>
                </div>
              </Tooltip>
            }
            rightIcon="ChevronLightDown"
            rightIconSize={12}
            data-testid="reporting-dates-chip"
          />
        }
      >
        <DropdownRadioGroup
          items={periodItems}
          selectedKey={activeReportingPeriod.reporting_date}
          tooltipProps={{ placement: 'right' }}
        />
      </DropdownMenu>
    </div>
  );
};
